import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { SITE_NAME } from "@/config";
import { collections } from "@/mocks/collections";

const FORM_URL = "https://readdy.ai/api/form/dag414bep053ijub23ag";

interface FormState {
  status: "idle" | "sending" | "success" | "error";
  msg: string;
}

export default function Footer() {
  const [state, setState] = useState<FormState>({ status: "idle", msg: "" });

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    // Anti-spam: silently ignore bot-filled submissions but show generic feedback.
    const trap = String(data.get("website_alt") || "").trim();
    if (trap) {
      setState({ status: "success", msg: "订阅成功，最新瓜料即将送达你的邮箱～" });
      form.reset();
      return;
    }

    const email = String(data.get("email") || "").trim();
    if (!email) {
      setState({ status: "error", msg: "请先填写你的邮箱地址。" });
      return;
    }

    setState({ status: "sending", msg: "" });
    const body = new URLSearchParams();
    body.set("email", email);

    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const meta = (parsed?.meta as Record<string, unknown> | undefined) || null;
      const msg = String(
        meta?.message || (parsed as { message?: string })?.message || meta?.detail || ""
      );
      const code = parsed?.code || "";

      if (res.ok && code === "OK") {
        setState({ status: "success", msg: "订阅成功，最新瓜料即将送达你的邮箱～" });
        form.reset();
      } else {
        setState({
          status: "error",
          msg: msg || "订阅暂未成功，请稍后再试。",
        });
      }
    } catch {
      setState({ status: "error", msg: "网络似乎开小差了，请稍后再试。" });
    }
  }

  return (
    <footer
      id="subscribe"
      className="scroll-mt-24 bg-background-800 text-foreground-100"
    >
      {/* Newsletter band */}
      <div className="border-b border-background-700/80">
        <div className="container-x flex flex-col gap-6 py-10 lg:flex-row lg:items-center lg:justify-between lg:py-12">
          <div className="max-w-md">
            <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-accent-400">
              <i className="ri-mail-send-line" /> 每周瓜田周报
            </p>
            <h3 className="mt-2 font-heading text-2xl font-bold text-foreground-50 md:text-3xl">
              大瓜不想错过？每周一准时送达
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-foreground-300">
              把你可能错过的实时吃瓜合集、热门盘点与行业观察，整理成一份清爽周报。随时可退订。
            </p>
          </div>
          <form
            data-readdy-form="chigua-newsletter"
            onSubmit={handleSubmit}
            className="w-full max-w-md"
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <label className="sr-only" htmlFor="footer-email">
                邮箱地址
              </label>
              <input
                id="footer-email"
                name="email"
                type="email"
                required
                autoComplete="email"
                placeholder="输入你的邮箱地址"
                className="w-full flex-1 rounded-md border border-background-600 bg-background-700/60 px-4 py-3 text-sm text-foreground-50 outline-none placeholder:text-foreground-500 focus:border-accent-500 focus:ring-2 focus:ring-accent-500/30"
              />
              <button
                type="submit"
                disabled={state.status === "sending"}
                className="inline-flex items-center justify-center whitespace-nowrap rounded-md bg-accent-500 px-5 py-3 text-sm font-bold text-background-950 transition-colors hover:bg-accent-400 disabled:opacity-60"
              >
                {state.status === "sending" ? "订阅中…" : "订阅周报"}
              </button>
            </div>
            {/* Anti-spam honeypot */}
            <div className="form-trap-wrap" aria-hidden="true">
              <label>
                站点备注
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                />
              </label>
            </div>
            {state.status === "success" ? (
              <p className="mt-3 text-sm text-accent-400" role="status">
                {state.msg}
              </p>
            ) : state.status === "error" ? (
              <p className="mt-3 text-sm text-red-300" role="alert">
                {state.msg}
              </p>
            ) : null}
          </form>
        </div>
      </div>

      {/* Link columns */}
      <div className="container-x grid grid-cols-2 gap-8 py-12 sm:grid-cols-2 lg:grid-cols-12">
        <div className="col-span-2 lg:col-span-4">
          <div className="flex items-center gap-2.5">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-500 font-heading text-xl font-bold text-background-50">
              瓜
            </span>
            <span className="font-heading text-2xl font-black text-foreground-50">
              {SITE_NAME}
            </span>
          </div>
          <p className="mt-4 max-w-xs text-sm leading-relaxed text-foreground-300">
            实时吃瓜的整理者。把散落在全网的热门大瓜、实时吃瓜合集与深度盘点归拢成一座清爽瓜田，蹲后续的你别迷路。
          </p>
          <div className="mt-5 flex items-center gap-3 text-foreground-300">
            {["ri-wechat-line", "ri-weibo-line", "ri-douban-line", "ri-youtube-line"].map(
              (icon) => (
                <a
                  key={icon}
                  href="#subscribe"
                  aria-label="社区入口"
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-background-600 text-lg transition-colors hover:border-accent-500 hover:text-accent-400"
                >
                  <i className={icon} />
                </a>
              )
            )}
          </div>
        </div>

        <div className="lg:col-span-3">
          <h4 className="text-sm font-bold text-foreground-50">专题合集</h4>
          <ul className="mt-4 space-y-2.5 text-sm">
            {collections.slice(0, 5).map((c) => (
              <li key={c.id}>
                <Link
                  to={`/collection/${c.id}`}
                  className="text-foreground-300 transition-colors hover:text-accent-400"
                >
                  {c.name}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-2">
          <h4 className="text-sm font-bold text-foreground-50">浏览</h4>
          <ul className="mt-4 space-y-2.5 text-sm">
            <li>
              <Link to="/" className="text-foreground-300 hover:text-accent-400">
                首页
              </Link>
            </li>
            <li>
              <Link to="/collection/featured" className="text-foreground-300 hover:text-accent-400">
                热门大瓜盘点
              </Link>
            </li>
            <li>
              <Link to="/about" className="text-foreground-300 hover:text-accent-400">
                关于本站
              </Link>
            </li>
            <li>
              <a href="#top" className="text-foreground-300 hover:text-accent-400">
                回到顶部
              </a>
            </li>
          </ul>
        </div>

        <div className="lg:col-span-3">
          <h4 className="text-sm font-bold text-foreground-50">合作与反馈</h4>
          <ul className="mt-4 space-y-2.5 text-sm text-foreground-300">
            <li className="flex items-start gap-2">
              <i className="ri-mail-line mt-0.5 text-accent-400" />
              <span>hello@chigua-daily.example.com</span>
            </li>
            <li className="flex items-start gap-2">
              <i className="ri-question-line mt-0.5 text-accent-400" />
              <span>来稿与选题建议，欢迎来信聊聊。</span>
            </li>
          </ul>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-background-700/80">
        <div className="container-x flex flex-col items-center justify-between gap-3 py-5 text-xs text-foreground-400 sm:flex-row">
          <p>© {new Date().getFullYear()} {SITE_NAME} · 本站内容仅供娱乐与信息参考。</p>
          <p className="flex items-center gap-1.5">
            <i className="ri-focus-3-line text-accent-500" />
            实时吃瓜 · 大瓜盘点 · 理性吃瓜
          </p>
        </div>
      </div>
    </footer>
  );
}